import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  Grid,
  Divider,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Alert,
} from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';

import PsychologyIcon from '@mui/icons-material/Psychology';
import ChatIcon from '@mui/icons-material/Chat';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import StorageIcon from '@mui/icons-material/Storage';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';

import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { JsonViewer } from '@/components/viewers';

interface Props {
  maxHeight?: number;
}

const KNOWN_PROVIDERS = ['OpenAI', 'AzureOpenAI', 'ClaudeAI', 'Gemini'];

const PRESET_MIME_TYPES = [
  { label: 'PDF Documents (application/pdf)', value: 'application/pdf' },
  { label: 'PNG Images (image/png)', value: 'image/png' },
  { label: 'JPEG Images (image/jpeg)', value: 'image/jpeg' },
  {
    label: 'Excel Spreadsheets (application/vnd.openxmlformats-officedocument.spreadsheetml.sheet)',
    value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    label: 'Word Documents (application/vnd.openxmlformats-officedocument.wordprocessingml.document)',
    value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  { label: 'Plain Text (text/plain)', value: 'text/plain' },
  { label: 'CSV Data (text/csv)', value: 'text/csv' },
  { label: 'JSON Data (application/json)', value: 'application/json' },
];

const getProviderColors = (provider: string) => {
  const norm = provider?.toLowerCase() || '';
  if (norm.includes('openai') && !norm.includes('azure')) {
    return { bg: 'rgba(16, 163, 127, 0.1)', border: 'rgba(16, 163, 127, 0.3)', text: '#10a37f', badge: '#10a37f' };
  }
  if (norm.includes('azure')) {
    return { bg: 'rgba(0, 120, 212, 0.1)', border: 'rgba(0, 120, 212, 0.3)', text: '#0078d4', badge: '#0078d4' };
  }
  if (norm.includes('claude') || norm.includes('anthropic')) {
    return { bg: 'rgba(217, 119, 6, 0.1)', border: 'rgba(217, 119, 6, 0.3)', text: '#d97706', badge: '#d97706' };
  }
  if (norm.includes('gemini') || norm.includes('google')) {
    return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)', text: '#8b5cf6', badge: '#8b5cf6' };
  }
  return { bg: 'rgba(100, 116, 139, 0.1)', border: 'rgba(100, 116, 139, 0.3)', text: '#64748b', badge: '#64748b' };
};

const formatNumber = (val?: number | null) => {
  if (val === undefined || val === null) return '—';
  if (val >= 1000000) return `${(val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
  return val.toLocaleString();
};

const LlmModelManagement = ({ maxHeight }: Props) => {
  const { getAllLlmModels, createLlmModel, updateLlmModel, deleteLlmModel } = useApiContext();
  const { toast } = useNotificationContext();
  const confirm = useConfirm();
  const theme = useTheme();
  const { t } = useTranslation();

  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<any>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [capabilityFilter, setCapabilityFilter] = useState<string>('all');

  // Drawer Inspection
  const [selectedModel, setSelectedModel] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [copiedCode, setCopiedCode] = useState<boolean>(false);

  // Add / Edit Dialog
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);

  const initialFormState = {
    id: undefined as number | string | undefined,
    modelName: '',
    modelCode: '',
    llmName: 'OpenAI',
    capabilities: ['conversation'] as string[],
    maxInputTokens: 128000 as number | '',
    maxOutputTokens: 4096 as number | '',
    restrictMimeTypes: [] as string[],
  };

  const [formData, setFormData] = useState(initialFormState);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setApiError(null);
      const data: any = await getAllLlmModels();
      const list: any[] = Array.isArray(data) ? data : ((data as any)?.data || []);
      const sanitized = list.map((m: any, index: number) => ({
        ...m,
        id: m.id ?? `${m.llmName}-${m.modelCode}-${index}`,
        maxOutputTokens: m.maxOutputTokens ?? m.maxOutputToken ?? null,
      }));
      setModels(sanitized);
    } catch (err: any) {
      setApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  // Summary Metrics
  const metrics = useMemo(() => {
    const total = models.length;
    const providers = new Set(models.map((m) => m.llmName)).size;
    const conversationCount = models.filter((m) => m.capabilities?.includes('conversation')).length;
    const embeddingCount = models.filter((m) => m.capabilities?.includes('embeddings')).length;
    const extractionCount = models.filter((m) => m.capabilities?.includes('documentDataExtraction')).length;
    return { total, providers, conversationCount, embeddingCount, extractionCount };
  }, [models]);

  // Unique Providers for Filter
  const availableProviders = useMemo(() => {
    const set = new Set<string>();
    models.forEach((m) => {
      if (m.llmName) set.add(m.llmName);
    });
    return Array.from(set).sort();
  }, [models]);

  // Filtered rows
  const filteredModels = useMemo(() => {
    return models.filter((m) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        m.modelName?.toLowerCase().includes(q) ||
        m.modelCode?.toLowerCase().includes(q) ||
        m.llmName?.toLowerCase().includes(q);

      const matchesProvider =
        providerFilter === 'all' || m.llmName?.toLowerCase() === providerFilter.toLowerCase();

      const matchesCapability =
        capabilityFilter === 'all' || m.capabilities?.includes(capabilityFilter);

      return matchesSearch && matchesProvider && matchesCapability;
    });
  }, [models, searchQuery, providerFilter, capabilityFilter]);

  // Open inspection drawer
  const handleOpenDrawer = (model: any) => {
    setSelectedModel(model);
    setDrawerTab(0);
    setDrawerOpen(true);
  };

  // Open Create Dialog
  const handleOpenCreate = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setFormError(null);
    setDialogOpen(true);
  };

  // Open Edit Dialog
  const handleOpenEdit = (model: any) => {
    setIsEditing(true);
    setFormData({
      id: model.id,
      modelName: model.modelName || '',
      modelCode: model.modelCode || '',
      llmName: model.llmName || 'OpenAI',
      capabilities: Array.isArray(model.capabilities) ? model.capabilities : [],
      maxInputTokens: model.maxInputTokens ?? '',
      maxOutputTokens: model.maxOutputTokens ?? model.maxOutputToken ?? '',
      restrictMimeTypes: Array.isArray(model.restrictMimeTypes) ? model.restrictMimeTypes : [],
    });
    setFormError(null);
    setDialogOpen(true);
  };

  // Save (Create or Update)
  const handleSaveModel = async () => {
    if (!formData.modelName.trim()) {
      setFormError('Model Name is required.');
      return;
    }
    if (!formData.modelCode.trim()) {
      setFormError('Model Code is required.');
      return;
    }
    if (!formData.llmName.trim()) {
      setFormError('Provider (LLM Name) is required.');
      return;
    }
    if (formData.capabilities.length === 0) {
      setFormError('Select at least one capability.');
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const payload = {
        modelName: formData.modelName.trim(),
        modelCode: formData.modelCode.trim(),
        llmName: formData.llmName.trim(),
        capabilities: formData.capabilities,
        maxInputTokens: formData.maxInputTokens !== '' ? Number(formData.maxInputTokens) : null,
        maxOutputTokens: formData.maxOutputTokens !== '' ? Number(formData.maxOutputTokens) : null,
        restrictMimeTypes: formData.restrictMimeTypes.length > 0 ? formData.restrictMimeTypes : null,
      };

      if (isEditing && formData.id) {
        await updateLlmModel(formData.id, payload);
        toast({ message: `Model "${payload.modelName}" updated successfully.`, type: 'success' });
      } else {
        await createLlmModel(payload);
        toast({ message: `Model "${payload.modelName}" added successfully.`, type: 'success' });
      }

      setDialogOpen(false);
      await fetchModels();

      if (drawerOpen && selectedModel?.id === formData.id) {
        setSelectedModel({ ...selectedModel, ...payload });
      }
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save model.');
    } finally {
      setSaving(false);
    }
  };

  // Delete Model
  const handleDeleteModel = async (model: any) => {
    try {
      await confirm({
        title: `Delete LLM Model`,
        description: `Are you sure you want to delete model "${model.modelName}" (${model.modelCode})? Any agents or pipelines relying on this model code may be affected.`,
        confirmationText: 'Delete Model',
        confirmationButtonProps: { color: 'error' },
        cancellationText: 'Cancel',
      });
    } catch {
      return;
    }

    try {
      setLoading(true);
      await deleteLlmModel(model.id);
      toast({ message: `Model "${model.modelName}" deleted.`, type: 'success' });
      if (drawerOpen && selectedModel?.id === model.id) {
        setDrawerOpen(false);
      }
      await fetchModels();
    } catch (err: any) {
      toast({ message: `Failed to delete model: ${err?.message || 'Unknown error'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Toggle Capability in Form
  const handleToggleCapability = (cap: string) => {
    setFormData((prev) => {
      const exists = prev.capabilities.includes(cap);
      const updated = exists ? prev.capabilities.filter((c) => c !== cap) : [...prev.capabilities, cap];
      return { ...prev, capabilities: updated };
    });
  };

  // Copy code helper
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Table Columns
  const columns: GridColDef[] = [
    {
      field: 'modelName',
      headerName: 'Model Name',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params: any) => {
        const row = params.row;
        const colors = getProviderColors(row.llmName);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: colors.bg,
                border: `1px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.text,
                flexShrink: 0,
              }}
            >
              <PsychologyIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 650, color: 'text.primary', lineHeight: 1.2 }}>
                {row.modelName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  fontSize: '11px',
                }}
              >
                {row.modelCode}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'llmName',
      headerName: 'Provider',
      width: 140,
      renderCell: (params: any) => {
        const provider = params.value || 'General';
        const colors = getProviderColors(provider);
        return (
          <Chip
            size="small"
            label={provider}
            sx={{
              fontWeight: 650,
              fontSize: '11px',
              bgcolor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
            }}
          />
        );
      },
    },
    {
      field: 'capabilities',
      headerName: 'Capabilities',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params: any) => {
        const caps: string[] = Array.isArray(params.value) ? params.value : [];
        if (!caps.length) return <Typography variant="caption" color="text.secondary">—</Typography>;

        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {caps.includes('conversation') && (
              <Chip
                size="small"
                icon={<ChatIcon sx={{ fontSize: '13px !important' }} />}
                label="Chat"
                sx={{
                  fontSize: '11px',
                  height: 22,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.dark,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                }}
              />
            )}
            {caps.includes('embeddings') && (
              <Chip
                size="small"
                icon={<DataObjectIcon sx={{ fontSize: '13px !important' }} />}
                label="Embeddings"
                sx={{
                  fontSize: '11px',
                  height: 22,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.dark,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                }}
              />
            )}
            {caps.includes('documentDataExtraction') && (
              <Chip
                size="small"
                icon={<DescriptionIcon sx={{ fontSize: '13px !important' }} />}
                label="Extraction"
                sx={{
                  fontSize: '11px',
                  height: 22,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.dark,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'maxInputTokens',
      headerName: 'Context Window',
      width: 140,
      renderCell: (params: any) => (
        <Typography variant="body2" sx={{ fontWeight: 550, fontFamily: 'monospace' }}>
          {params.value ? `${formatNumber(params.value)} tokens` : '—'}
        </Typography>
      ),
    },
    {
      field: 'maxOutputTokens',
      headerName: 'Max Output',
      width: 120,
      renderCell: (params: any) => (
        <Typography variant="body2" sx={{ fontWeight: 550, fontFamily: 'monospace' }}>
          {params.value ? `${formatNumber(params.value)} tokens` : '—'}
        </Typography>
      ),
    },
    {
      field: 'restrictMimeTypes',
      headerName: 'Multimodal / MIME',
      width: 150,
      renderCell: (params: any) => {
        const types = params.value;
        if (!types || (Array.isArray(types) && types.length === 0)) {
          return (
            <Chip
              size="small"
              label="Standard"
              sx={{ fontSize: '11px', height: 20, bgcolor: 'action.hover', color: 'text.secondary' }}
            />
          );
        }
        return (
          <Tooltip title={Array.isArray(types) ? types.join(', ') : String(types)}>
            <Chip
              size="small"
              label={`${Array.isArray(types) ? types.length : 1} MIME types`}
              sx={{
                fontSize: '11px',
                height: 20,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: any) => [
        <GridActionsCellItem
          key="view"
          icon={
            <Tooltip title="View Specifications">
              <VisibilityIcon fontSize="small" />
            </Tooltip>
          }
          label="View"
          onClick={() => handleOpenDrawer(params.row)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={
            <Tooltip title="Edit Model">
              <EditIcon fontSize="small" />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleOpenEdit(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={
            <Tooltip title="Delete Model">
              <DeleteOutlineIcon fontSize="small" color="error" />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDeleteModel(params.row)}
        />,
      ],
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: maxHeight || '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {/* ─── Top Header & Primary Action ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 750, color: 'text.primary', letterSpacing: '-0.01em' }}>
              LLM Model Management
            </Typography>
            <Chip
              size="small"
              label={`${metrics.total} Registered`}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.main,
                fontWeight: 700,
                fontSize: '11px',
                height: 22,
              }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 450, mt: 0.25 }}>
            Configure and manage Foundation Models, Vector Embedding engines, and extraction models.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            onClick={fetchModels}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: 'divider',
              color: 'text.primary',
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.7)', borderColor: theme.palette.secondary.main },
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={handleOpenCreate}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              boxShadow: `0 4px 14px ${alpha(theme.palette.secondary.main, 0.25)}`,
            }}
          >
            Add Model
          </Button>
        </Stack>
      </Stack>

      {/* ─── KPI Metric Cards ─── */}
      {/* ─── KPI Metric Cards ─── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.5,
        }}
      >
        <Card
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            border: 1,
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Total Models
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mt: 0.25 }}>
            {metrics.total}
          </Typography>
        </Card>

        <Card
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            border: 1,
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Providers
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.25 }}>
            {metrics.providers}
          </Typography>
        </Card>

        <Card
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            border: 1,
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Conversation
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'info.main', mt: 0.25 }}>
            {metrics.conversationCount}
          </Typography>
        </Card>

        <Card
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            border: 1,
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Embeddings
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'secondary.main', mt: 0.25 }}>
            {metrics.embeddingCount}
          </Typography>
        </Card>

        <Card
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            border: 1,
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            Extraction
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.main', mt: 0.25 }}>
            {metrics.extractionCount}
          </Typography>
        </Card>
      </Box>

      {/* ─── Filter & Search Bar ─── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <TextField
          size="small"
          placeholder="Search models by name, code, or provider..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            minWidth: { xs: '100%', md: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.85rem',
            },
          }}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {/* Provider Filter Chips */}
          <Chip
            size="small"
            label="All Providers"
            clickable
            onClick={() => setProviderFilter('all')}
            variant={providerFilter === 'all' ? 'filled' : 'outlined'}
            color={providerFilter === 'all' ? 'secondary' : 'default'}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
          />
          {availableProviders.map((p) => {
            const isSel = providerFilter.toLowerCase() === p.toLowerCase();
            return (
              <Chip
                key={p}
                size="small"
                label={p}
                clickable
                onClick={() => setProviderFilter(isSel ? 'all' : p)}
                variant={isSel ? 'filled' : 'outlined'}
                color={isSel ? 'secondary' : 'default'}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
              />
            );
          })}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />

          {/* Capability Filter Chips */}
          <Chip
            size="small"
            label="All Capabilities"
            clickable
            onClick={() => setCapabilityFilter('all')}
            variant={capabilityFilter === 'all' ? 'filled' : 'outlined'}
            color={capabilityFilter === 'all' ? 'primary' : 'default'}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
          />
          <Chip
            size="small"
            label="Chat"
            clickable
            onClick={() => setCapabilityFilter(capabilityFilter === 'conversation' ? 'all' : 'conversation')}
            variant={capabilityFilter === 'conversation' ? 'filled' : 'outlined'}
            color={capabilityFilter === 'conversation' ? 'info' : 'default'}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
          />
          <Chip
            size="small"
            label="Embeddings"
            clickable
            onClick={() => setCapabilityFilter(capabilityFilter === 'embeddings' ? 'all' : 'embeddings')}
            variant={capabilityFilter === 'embeddings' ? 'filled' : 'outlined'}
            color={capabilityFilter === 'embeddings' ? 'secondary' : 'default'}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
          />
          <Chip
            size="small"
            label="Extraction"
            clickable
            onClick={() =>
              setCapabilityFilter(capabilityFilter === 'documentDataExtraction' ? 'all' : 'documentDataExtraction')
            }
            variant={capabilityFilter === 'documentDataExtraction' ? 'filled' : 'outlined'}
            color={capabilityFilter === 'documentDataExtraction' ? 'success' : 'default'}
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '11px' }}
          />
        </Stack>
      </Stack>

      {/* ─── Table Area ─── */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {apiError && (
          <Box sx={{ mb: 1.5 }}>
            {apiError?.message?.toLowerCase().includes('network') ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={fetchModels} sx={{ fontWeight: 700 }}>
                    Retry
                  </Button>
                }
                sx={{ borderRadius: '12px' }}
              >
                <strong>Unable to reach LLM API service:</strong> Ensure the <code>minotii-llm-api</code> service is running on port 3003 (<code>http://localhost:3003</code>).
              </Alert>
            ) : (
              <ErrorMessage error={apiError} onClose={() => setApiError(null)} />
            )}
          </Box>
        )}
        {loading && !models.length ? (
          <ProgressIndicator />
        ) : (
          <DataGridTable
            rows={filteredModels}
            columns={columns}
            height="100%"
            loading={loading}
            pageSize={10}
          />
        )}
      </Box>

      {/* ─── Inspection DrawerPanel ─── */}
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="right"
        minWidth="45vw"
        title={selectedModel?.modelName || 'Model Specifications'}
        subTitle={selectedModel?.modelCode}
      >
        {selectedModel && (
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Top Badge Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: 1,
                borderColor: 'divider',
                bgcolor: getProviderColors(selectedModel.llmName).bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '12px',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: getProviderColors(selectedModel.llmName).text,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <PsychologyIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {selectedModel.modelName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={selectedModel.llmName}
                      sx={{
                        fontWeight: 700,
                        fontSize: '11px',
                        height: 20,
                        bgcolor: getProviderColors(selectedModel.llmName).border,
                        color: getProviderColors(selectedModel.llmName).text,
                      }}
                    />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {selectedModel.modelCode}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy Model Code">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyCode(selectedModel.modelCode)}
                    sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
                  >
                    {copiedCode ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenEdit(selectedModel)}
                  startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 650, fontSize: '0.8rem' }}
                >
                  Edit
                </Button>
              </Stack>
            </Paper>

            {/* Tabs */}
            <Tabs
              value={drawerTab}
              onChange={(_, v) => setDrawerTab(v)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 650, fontSize: '0.85rem' },
              }}
            >
              <Tab label="Specifications" />
              <Tab label="Pipeline Config" />
              <Tab label="Raw JSON" />
            </Tabs>

            {drawerTab === 0 && (
              <Stack spacing={2}>
                {/* Capabilities Card */}
                <Card elevation={0} sx={{ p: 2, borderRadius: '16px', border: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Supported Capabilities
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedModel.capabilities?.map((c: string) => (
                      <Chip
                        key={c}
                        label={
                          c === 'conversation'
                            ? 'Conversation / Chat Completion'
                            : c === 'embeddings'
                              ? 'Dense Vector Embeddings'
                              : c === 'documentDataExtraction'
                                ? 'Document Data Extraction'
                                : c
                        }
                        color={
                          c === 'conversation'
                            ? 'info'
                            : c === 'embeddings'
                              ? 'secondary'
                              : c === 'documentDataExtraction'
                                ? 'success'
                                : 'default'
                        }
                        sx={{ fontWeight: 600, fontSize: '12px' }}
                      />
                    ))}
                  </Stack>
                </Card>

                {/* Token Limits Card */}
                <Card elevation={0} sx={{ p: 2, borderRadius: '16px', border: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Token Capacity & Limits
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        MAX INPUT TOKENS (CONTEXT)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                        {selectedModel.maxInputTokens ? selectedModel.maxInputTokens.toLocaleString() : 'Not constrained'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        MAX OUTPUT TOKENS
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                        {selectedModel.maxOutputTokens ? selectedModel.maxOutputTokens.toLocaleString() : 'Default'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                {/* Multimodal & MIME Types Card */}
                <Card elevation={0} sx={{ p: 2, borderRadius: '16px', border: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Multimodal & MIME Type Restrictions
                  </Typography>
                  {selectedModel.restrictMimeTypes && selectedModel.restrictMimeTypes.length > 0 ? (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        This model is restricted to processing these specific document / media formats:
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {selectedModel.restrictMimeTypes.map((mime: string) => (
                          <Chip
                            key={mime}
                            label={mime}
                            size="small"
                            sx={{ fontFamily: 'monospace', fontSize: '11px', bgcolor: 'action.hover' }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: '12px', fontSize: '0.85rem' }}>
                      No MIME type restrictions configured. Model accepts all provider-supported formats.
                    </Alert>
                  )}
                </Card>
              </Stack>
            )}

            {drawerTab === 1 && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Use this JSON snippet in tenant settings, agent configurations, or pipeline step parameters to reference this model:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'grey.900',
                    color: 'common.white',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    position: 'relative',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(
                      {
                        LLM_NAME: selectedModel.llmName,
                        LLM_MODEL: selectedModel.modelCode,
                        MAX_INPUT_TOKENS: selectedModel.maxInputTokens || 128000,
                        MAX_OUTPUT_TOKENS: selectedModel.maxOutputTokens || 4096,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </Paper>
              </Stack>
            )}

            {drawerTab === 2 && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', p: 1.5, bgcolor: 'background.paper' }}>
                <JsonViewer json={selectedModel} maxHeight={450} showLabel={false} />
              </Box>
            )}
          </Box>
        )}
      </DrawerPanel>

      {/* ─── Add / Edit Model Dialog ─── */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 1,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem', pb: 1 }}>
          {isEditing ? 'Edit LLM Model' : 'Register New LLM Model'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError && (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {formError}
            </Alert>
          )}

          {/* Provider / LLM Name */}
          <Autocomplete
            freeSolo
            options={KNOWN_PROVIDERS}
            value={formData.llmName}
            onChange={(_, val) => setFormData((p) => ({ ...p, llmName: val || '' }))}
            onInputChange={(_, val) => setFormData((p) => ({ ...p, llmName: val }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Provider / LLM Name *"
                placeholder="e.g. OpenAI, AzureOpenAI, ClaudeAI, Gemini"
                helperText="Select an established AI provider or type a custom provider identifier"
                size="small"
              />
            )}
          />

          {/* Model Name */}
          <TextField
            label="Model Name *"
            placeholder="e.g. GPT-4o, Claude 3.5 Sonnet, Gemini 1.5 Pro"
            value={formData.modelName}
            onChange={(e) => setFormData((p) => ({ ...p, modelName: e.target.value }))}
            helperText="Friendly display name shown throughout the application"
            size="small"
          />

          {/* Model Code */}
          <TextField
            label="Model Code *"
            placeholder="e.g. gpt-4o, claude-3-5-sonnet-20241022, gemini-1.5-pro"
            value={formData.modelCode}
            onChange={(e) => setFormData((p) => ({ ...p, modelCode: e.target.value }))}
            helperText="Exact API model string sent to the provider endpoint"
            size="small"
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />

          {/* Capabilities */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Model Capabilities *
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.includes('conversation')}
                    onChange={() => handleToggleCapability('conversation')}
                    color="info"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 550 }}>Chat / Conversation</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.includes('embeddings')}
                    onChange={() => handleToggleCapability('embeddings')}
                    color="secondary"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 550 }}>Embeddings</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.includes('documentDataExtraction')}
                    onChange={() => handleToggleCapability('documentDataExtraction')}
                    color="success"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 550 }}>Data Extraction</Typography>}
              />
            </Stack>
          </Box>

          {/* Token Limits */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              label="Max Input Tokens"
              type="number"
              placeholder="128000"
              value={formData.maxInputTokens}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  maxInputTokens: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              helperText="Context window limit (tokens)"
              size="small"
              fullWidth
            />
            <TextField
              label="Max Output Tokens"
              type="number"
              placeholder="4096"
              value={formData.maxOutputTokens}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  maxOutputTokens: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              helperText="Generation ceiling limit (tokens)"
              size="small"
              fullWidth
            />
          </Box>

          {/* Restricted MIME Types */}
          <Autocomplete
            multiple
            freeSolo
            options={PRESET_MIME_TYPES.map((o) => o.value)}
            value={formData.restrictMimeTypes}
            onChange={(_, val) => setFormData((p) => ({ ...p, restrictMimeTypes: val }))}
            renderTags={(val, getTagProps) =>
              val.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Restricted MIME Types (Optional)"
                placeholder="e.g. application/pdf, image/png"
                helperText="Leave empty if model accepts all formats"
                size="small"
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveModel}
            disabled={saving}
            variant="contained"
            color="secondary"
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5 }}
          >
            {saving ? 'Saving...' : isEditing ? 'Update Model' : 'Register Model'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LlmModelManagement;
